import React, { useEffect, useState, useRef, useMemo } from "react";
import { Route, Routes, Navigate, Outlet, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import Cookies from "universal-cookie";

import Appbar from "./Features/Appbar/Appbar";
import SessionExpired from "./Features/Dialog/SessionExpired";

import DigitalTwin from "./Pages/DigitalTwinV3/DigitalTwin";
import Components from "./Static/Data/_Common/Components";
import theme from "./Theme/BaseTheme";
import Login from "./Auth/Login";
import ForgotPassword from "./Auth/ForgotPassword";
import ResetPassword from "./Auth/ResetPassword";

import {
    RemoveCookies,
    IsSessionExpired,
} from "./Helper/CookieAndNav/CookieAndNav";

import { useSelector, useDispatch } from "react-redux";
import AddConfiguration from "./PagesContent/DigitalTwinList/AddConfiguration";
import BatteryOnBoardingForm from "./PagesContent/DigitalTwinList/BatteryOnBoardingForm";
import RecordReplacementPage from "./PagesContent/RecordReplacement/RecordReplacementPage";
import EditPackDetailsPage from "./PagesContent/EditPackDetails/EditPackDetailsPage";
import ReplacementLogDetailPage from "./PagesContent/RecordReplacement/ReplacementLogDetailPage";
import { ResetFilters, SetIsFiltersMounted } from "./Actions/Filters";
import { SetSearchID, SetSearchType } from "./Actions/Search";
import AlertDrillDown from "./Pages/AlertDrillDown/AlertDrillDown";
import ChargerDrillDown from "./Pages/ChargerDrillDown/ChargerDrillDown";
import InventoryPage from "./Pages/Inventory/Inventory";
import OverviewPage from "./Pages/Overview/Overview";
import FleetPerformancePage from "./Pages/FleetPerformance/FleetPerformance";
import { mergeUserTranslations, i18n } from './i18n';
import { ToastProvider } from "./Hooks/useToast";
import AdvancedAnalysis from "./Pages/AdvancedAnalysis/AdvancedAnalysis";
import AppCache from "./Helper/Common/AppCache";
import { PermissionProvider } from "./Context/PermissionContext";
import { DashboardProvider, useDashboard } from "./Context/DashboardContext";
import { SetUserPermissions } from "./Actions/PermissionActions";
import ProtectedRoute from "./Components/ProtectedRoute";
import { routeFeatureCodes, FEATURE_CODES } from "./Static/FeatureMapping";
import PermissionService from "./Services/PermissionService";
import CustomDashboard from "./PagesContent/CustomObservability/CustomDashboard";
import CustomInventoryPage from "./PagesContent/CustomObservability/CustomInventoryPage";
import CustomDigitalTwinPage from "./PagesContent/CustomObservability/CustomDigitalTwinPage";
import CustomBMSAlertV2Page from "./PagesContent/CustomObservability/CustomBMSAlertV2Page";
import AlertSettingsPage from "./Pages/BmsFaults/AlertSettingsPage";
import PlatformAlerts from "./Pages/PlatformAlerts/PlatformAlerts";
import AccessGrantedEntities from "./Pages/OrgSettings/AccessGrantedEntities";
import NetworkEntityPage from "./Pages/AssetManagement/NetworkEntityPage";
import NewWorkOrderPage from "./Pages/AssetManagement/NewWorkOrderPage";
import usePageTracking from "./Analytics/usePageTracking";

/**
 * MainDashboardGuard — blocks main-dashboard routes for custom-only users.
 *
 * A user is "custom-only" when their org has no main navigation pages
 * (navigationCard is empty) but does have custom platform pages.
 * Redirects to /custom-observability, preserving the query string.
 *
 * Supports two usage patterns:
 *   - Layout route: <Route element={<MainDashboardGuard />}> — renders <Outlet />
 *   - Wrapper:      <MainDashboardGuard><Child /></MainDashboardGuard> — renders children
 */
function MainDashboardGuard({ children }) {
    const location = useLocation();
    const { activeDashboard } = useDashboard();
    const navigationCard = AppCache.getItem("navigationCard") || [];
    const customNavigationCard = AppCache.getItem("customNavigationCard") || [];

    const isCustomOnly = navigationCard.length === 0 && customNavigationCard.length > 0;

    if (isCustomOnly || activeDashboard !== 'coulomb') {
        return <Navigate to={`/custom-observability${location.search}`} replace />;
    }

    return children ?? <Outlet />;
}

/**
 * DefaultRoute — renders the correct landing page based on active dashboard.
 * Must be used inside DashboardProvider.
 */
function DefaultRoute({ pages, pagesMeta }) {
    const { activeDashboard, availableDashboards } = useDashboard();

    if (activeDashboard !== 'coulomb') {
        const activeDash = availableDashboards.find(d => d.key === activeDashboard);
        return <Navigate to={activeDash?.route || '/custom-observability'} replace />;
    }

    const formattedPages = (pages || []).map((item) => item.toLowerCase());

    if (formattedPages.includes("overview")) {
        return (
            <OverviewPage
                pagesContent={pagesMeta}
                permissions={pagesMeta?.find((x) => x.mykey === "Overview")?.permissions}
            />
        );
    }

    return (
        <InventoryPage
            pagesContent={pagesMeta}
            permissions={pagesMeta?.find((x) => x.mykey === "DigitalTwin")?.permissions}
        />
    );
}

function App() {
    const cookies = new Cookies();
    const [LoggedIn, setLoggedIn] = useState(false);
    const [translationsLoaded, setTranslationsLoaded] = useState(false);
    const isSessionExpired = useSelector((state) => state.SessionExpired.value);
    const dispatch = useDispatch();
    const location = useLocation();
    const previousPath = useRef(location.pathname);

    // Amplitude page views / time on page for every route, including pages that never
    // call Events() themselves.
    usePageTracking();

    const accessToken = cookies.get("access_token");
    const pages = AppCache.getItem("pages");
    const pagesMeta = AppCache.getItem("pagesMeta");
    const name = cookies.get("name");
    const finalCall = cookies.get("finalCall");

    // CRITICAL: Restore permissions SYNCHRONOUSLY before any render
    // This must run before child components check permissions
    useMemo(() => {
        const rbacVersion = localStorage.getItem('rbac_version');
        const rbacEnabled = localStorage.getItem('rbac_enabled') === 'true';

        if (rbacVersion === 'v2' && rbacEnabled) {
            const storedPermissions = localStorage.getItem('rbac_permissions');
            if (storedPermissions) {
                try {
                    const permissionsMap = JSON.parse(storedPermissions);

                    // Update PermissionService IMMEDIATELY (before any render)
                    PermissionService.permissionsMap = permissionsMap;

                    // Restore to AppCache
                    AppCache.setItem('rbacVersion', 'v2');
                    AppCache.setItem('rbacEnabled', true);
                } catch (error) {
                    console.error('[App] Failed to restore permissions:', error);
                }
            }
        } else {
            AppCache.setItem('rbacVersion', 'v1');
            AppCache.setItem('rbacEnabled', false);
        }
        return true; // useMemo requires a return value
    }, []); // Empty array = runs once on mount, but synchronously during render

    // Custom Hook: Check session expiration
    useEffect(() => {
        if (isSessionExpired) {
            IsSessionExpired();
        }
    }, [isSessionExpired]);

    // Custom Hook: Load translations
    useEffect(() => {
        const loadUserTranslations = async () => {
            if (accessToken) {
                await mergeUserTranslations("userId");
            }
            setTranslationsLoaded(true);
        };
        loadUserTranslations();
    }, [LoggedIn]);

    // Custom Hook: Reset filters on location change
    useEffect(() => {
        if (previousPath.current !== location.pathname) {
            dispatch(ResetFilters());
            dispatch(SetSearchID(""));
            dispatch(SetSearchType(""));
            dispatch(SetIsFiltersMounted(false));
            previousPath.current = location.pathname;
        }
    }, [location.pathname]);

    // Custom Hook: Update Redux with permissions (runs after synchronous restoration)
    useEffect(() => {
        const rbacVersion = localStorage.getItem('rbac_version');
        const rbacEnabled = localStorage.getItem('rbac_enabled') === 'true';

        if (rbacVersion === 'v2' && rbacEnabled) {
            const storedPermissions = localStorage.getItem('rbac_permissions');
            if (storedPermissions) {
                try {
                    const permissionsMap = JSON.parse(storedPermissions);
                    // Update Redux (PermissionService already updated synchronously in useMemo)
                    dispatch(SetUserPermissions(permissionsMap));
                } catch (error) {
                    console.error('[App] Failed to parse permissions:', error);
                }
            }
        }
    }, [dispatch]);

    const dynamicRoutes = useMemo(() => {
        if (!pages || !pagesMeta) return [];

        return pagesMeta.map((item, index) => {
            if (!pages.includes(item.mykey)) return null;
            const Component = Components[item.mykey];
            const permissions = item.permissions;
            // Array for an any-of route, undefined when unmapped -- the
            // existing `featureCode ? ... : ...` branches below read both the
            // same way, and ProtectedRoute accepts either shape.
            const codes = routeFeatureCodes(item.to);
            const featureCode = codes.length > 0 ? codes : undefined;

            if (item.queryParam && Object.keys(item.queryParam).length > 0) {
                const [key, value] = Object.entries(item.queryParam)[0];
                const path = `${item.to}/${key}=${value}`;
                const element = featureCode
                    ? <ProtectedRoute featureCode={featureCode}><Component batteryType={value} permissions={permissions} /></ProtectedRoute>
                    : <Component batteryType={value} permissions={permissions} />;
                return <Route path={path} key={index} element={element} />;
            }

            const element = featureCode
                ? <ProtectedRoute featureCode={featureCode}><Component pagesContent={pagesMeta} permissions={permissions} /></ProtectedRoute>
                : <Component pagesContent={pagesMeta} permissions={permissions} />;
            return <Route path={item.to} key={index} element={element} />;
        });
    }, [pages, pagesMeta]);

    if (!accessToken || !name || !pagesMeta || !pages || finalCall) {
        if (finalCall) RemoveCookies();

        return (
            <ThemeProvider theme={theme}>
                <Routes>
                    <Route path="/forgot_password" element={<ForgotPassword />} />
                    <Route path="/reset_password" element={<ResetPassword />} />
                    <Route path="/" element={<Login setLoggedIn={setLoggedIn} />} />
                    <Route path="*" element={<Login setLoggedIn={setLoggedIn} />} />
                </Routes>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <PermissionProvider>
                <DashboardProvider>
                    <ToastProvider>
                        <Appbar appbarLinks={pagesMeta} />
                        <Routes>
                            {/* All main-dashboard routes — guarded for custom-only users */}
                            <Route element={<MainDashboardGuard />}>
                                {dynamicRoutes}
                                <Route path="/specificBattery" element={<DigitalTwin />} />
                                <Route path="/inventory" element={<InventoryPage />} />
                                <Route path="/alertDrillDown" element={<AlertDrillDown />} />
                                <Route path="/ChargerDrillDown" element={<ChargerDrillDown />} />
                                <Route
                                    path="/digitalTwin/add-config"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.DIGITAL_TWIN}>
                                            <AddConfiguration
                                                pagesContent={pagesMeta}
                                                permissions={pagesMeta?.find((x) => x.mykey === "DigitalTwin")?.permissions}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/digitalTwin/add-battery"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.DIGITAL_TWIN}>
                                            <BatteryOnBoardingForm
                                                pagesContent={pagesMeta}
                                                permissions={pagesMeta?.find((x) => x.mykey === "DigitalTwin")?.permissions}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/record-replacement"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.BATTERY_COMPONENTS}>
                                            <RecordReplacementPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/edit-pack-details"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.BATTERY_COMPONENTS}>
                                            <EditPackDetailsPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/replacement-log"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.BATTERY_COMPONENTS}>
                                            <ReplacementLogDetailPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/advanced-analysis" element={<AdvancedAnalysis />} />
                                {/* Raising a work order has its own URL rather
                                    than a modal: a request can carry hundreds
                                    of serials and the per-serial report that
                                    comes back on failure has to render beside
                                    the form that produced it. Both routes
                                    render the SAME page; the edit one fetches
                                    the detail first, because the list row
                                    carries no movements. */}
                                <Route
                                    path="/asset-management/work-orders/new"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.ASSET_MANAGEMENT_WORK_ORDERS}>
                                            <NewWorkOrderPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/asset-management/work-orders/:workOrderID/edit"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.ASSET_MANAGEMENT_WORK_ORDERS}>
                                            <NewWorkOrderPage />
                                        </ProtectedRoute>
                                    }
                                />
                                {/* One network entity, on its own URL. It is
                                    linked to from the Assets tab's Current
                                    Custody cell as well as from the Network
                                    row, and a thing reached from two places
                                    needs an address. */}
                                <Route
                                    path="/asset-management/network/:entityID"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.ASSET_MANAGEMENT_NETWORK_ENTITIES}>
                                            <NetworkEntityPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/platform-alerts-v2"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.PLATFORM_ALERTS_V2}>
                                            <PlatformAlerts />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/org-settings/client-api-management/:id"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.CLIENT_API_MANAGEMENT}>
                                            <AccessGrantedEntities />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route            
                                    path="/platform-alerts-v2/alert-settings"
                                    element={
                                        <ProtectedRoute featureCode={FEATURE_CODES.PLATFORM_ALERTS_V2_SETTINGS}>
                                            <AlertSettingsPage />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            {/* Custom-observability routes — no guard, Indigo users land here */}
                            <Route path="/custom-observability" element={<CustomDashboard />} />
                            <Route path="/custom-observability/inventory" element={<CustomInventoryPage />} />
                            <Route path="/custom-observability/specificBattery" element={<CustomDigitalTwinPage />} />
                            <Route
                                path="/custom-observability/bms-faults"
                                element={
                                    <ProtectedRoute featureCode={FEATURE_CODES.INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS}>
                                        <CustomBMSAlertV2Page />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="*" element={<DefaultRoute pages={pages} pagesMeta={pagesMeta} />} />
                        </Routes>

                        {isSessionExpired && (
                            <SessionExpired isSessionExpired={isSessionExpired} />
                        )}
                    </ToastProvider>
                </DashboardProvider>
            </PermissionProvider>
        </ThemeProvider>
    );
}

export default App;
